// how-to-start/deck-build-runner.tsx
//
// THE REST OF THE DECK, generated into the editor the user is already looking
// at.
//
// The funnel plans a deck, generates its first slide, creates the story and
// opens the editor (see ./components/generating.tsx). Everything after slide
// one happens here: each slide is asked for, and dropped into the open
// document the moment its turn comes.
//
// WHY IT IS MOUNTED IN THE APP ROOT. It has to outlive the funnel that starts
// it AND be alive in the editor that receives the slides, and nothing else in
// the tree is both. BaseLayout is not: src/core/routes/editor-routes.tsx
// renders the editor deliberately outside it, so a runner mounted there would
// unmount at exactly the moment its work becomes visible. src/app.tsx renders
// <Outlet /> and survives every navigation, so it lives there.
//
// ORDER IS CORRECTNESS HERE, NOT POLISH. Slides are generated many at a time
// and finish in whatever order the backend returns them, while every insert
// lands at the END of the deck. Placing slide 5 the moment it arrives would
// put it before slide 4 permanently. So arrivals are buffered and released
// strictly in sequence: slide N goes in only once 1..N-1 have.

import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { store } from "@store";
import { versionsApi } from "@api/versions";
import { usePublish } from "@/providers/hooks/use-publish";
import { createEntity, deleteEntity, updateStorySettings } from "@store/story";
import type { StaticEntityType } from "@config";
import { normalizedFromNestedStructured } from "@/shared";
import { useFunnelGeneration } from "./hooks/use-funnel-generation";
import type { DeckPlan } from "./hooks/use-funnel-generation";
import {
  clearFailedSlides,
  noteFailedSlide,
  useDeckBuildStore,
} from "./store/deck-build.store";
import type { DeckBuildJob } from "./store/deck-build.store";
import { applyBrandLogo } from "./utils/apply-brand-logo";
import { nextRevealStep } from "./utils/reveal-queue";
import { slideIndexFor } from "./utils/slide-index";
import {
  SLIDE_GENERATION_CONCURRENCY,
  delay,
  runWithConcurrency,
} from "./utils/run-with-concurrency";
import {
  SLIDE_BUILD_MAX_STEPS,
  SLIDE_BUILD_STEP_MS,
  SLIDE_SETTLE_MS,
  afterTwoFrames,
  buildUnitsOf,
  clearAllArriving,
  clearArriving,
  holdSubtreeInvisible,
  prefersReducedMotion,
  releaseHold,
  revealArriving,
} from "@editor/hooks/slide-arrival";
import { LIVE_ATTR } from "@editor/modules/editor/components/deck-build-shield";

// HOW A SLIDE ARRIVES.
//
// Not all at once. A finished slide appearing whole reads as a paste, and
// twelve of them in a row read as a page reloading. Each slide is built
// instead -- its elements resolving into place one after another -- and the
// next slide does not begin until the one before it has finished. The deck
// looks like it is being made, which is what it is.
//
// Slower than the content strictly needs to be, deliberately: generation of a
// whole deck takes well over a minute, so the build has time to spend and
// nothing is gained by racing it.

/**
 * The gap when the reader has asked for less motion.
 *
 * The stylesheet drops the animation for them, so there is nothing to wait
 * for -- but the slides should still arrive one at a time rather than in a
 * heap, which is information rather than decoration.
 */
const REDUCED_MOTION_INTERVAL_MS = 400;

/** How often to re-check whether the slide currently due has arrived. */
const REVEAL_POLL_MS = 100;

/**
 * How long the build stops following after the user last looked around --
 * scrolled the page, by wheel, touch, scrollbar or a scroll key. Long enough
 * to read a slide; short enough that one stray click does not leave them on
 * slide 1 while the rest of the deck arrives out of sight.
 */
const FOLLOW_RESUME_MS = 8000;

/**
 * The inputs that mean the user is looking somewhere of their own choosing.
 *
 * Only inputs that MOVE THE VIEW. The editor sits under a page-wide shield
 * while the deck builds (editor/components/deck-build-shield), so a click or
 * an ordinary key does nothing -- and counting them, as this once did with
 * every pointerdown and keydown, paused the follow for FOLLOW_RESUME_MS on
 * every frustrated click, and the deck stopped scrolling with the build.
 * A pointerdown still counts when it lands outside the shield and the banner:
 * the page's scrollbar, which the fixed shield does not cover.
 */
const LOOK_AROUND_EVENTS = [
  "wheel",
  "touchmove",
  "keydown",
  "pointerdown",
] as const;

/** Keys the browser scrolls the page with; any other key moves nothing. */
const SCROLL_KEYS: ReadonlySet<string> = new Set([
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

/**
 * Whether to skip the build animation: the reader asked for less motion, or
 * nobody is looking -- a hidden tab neither paints nor times animations, so
 * playing one there only holds the deck up. (The helpers it builds on live in
 * @editor/hooks/slide-arrival, shared with the agent's deck build.)
 */
const skipBuildAnimation = () =>
  prefersReducedMotion() ||
  (typeof document !== "undefined" && document.visibilityState === "hidden");

/** How long to wait for the editor to finish opening the story before giving up. */
const STORY_LOAD_TIMEOUT_MS = 60_000;
const STORY_POLL_INTERVAL_MS = 150;

/**
 * Whether the editor currently has this deck open.
 *
 * A deck built into a standalone version shares its story -- the user's
 * hidden host page -- with every other standalone version they have, so the
 * story id alone would say yes with the wrong version on screen.
 */
/**
 * WHICH DOCUMENT THIS DECK IS BEING BUILT INTO.
 *
 * The story id alone does not say: every standalone version a user makes
 * hangs off the same hidden host page (see features/standalone-versions), so
 * two decks built one after the other read as the same document -- and the
 * second one inherited the first one's plan, and never started a run of its
 * own. A user who asked for three slides watched fifteen arrive.
 */
const jobKey = (job: Pick<DeckBuildJob, "storyId" | "versionId">) =>
  `${job.storyId}:${job.versionId ?? ""}`;

const storyIsOpen = (job: Pick<DeckBuildJob, "storyId" | "versionId">) => {
  const state = store.getState().story;
  const openStoryId = (state?.story as { _id?: string } | null)?._id;
  if (openStoryId !== job.storyId) return false;
  const openVersionId =
    (state?.version as { _id?: string } | null | undefined)?._id ?? null;
  return openVersionId === (job.versionId ?? null);
};

/**
 * Resolves once the editor has the story open, or false if it never does.
 *
 * The funnel navigates and the runner starts in the same breath, so the story
 * is virtually never loaded yet when the first slide is ready to place.
 */
const waitForStory = async (
  job: Pick<DeckBuildJob, "storyId" | "versionId">,
  signal: AbortSignal,
) => {
  const deadline = Date.now() + STORY_LOAD_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (signal.aborted) return false;
    if (storyIsOpen(job)) return true;
    await new Promise((resolve) => setTimeout(resolve, STORY_POLL_INTERVAL_MS));
  }
  return false;
};

/**
 * Names the story after its deck, once the plan has said what that is.
 *
 * The story was created before the outline was read, so it opened under its
 * document type -- "sales deck", or plainly "document" for a deck built from
 * one of the user's own. Only a title still reading that placeholder is
 * replaced: one the user has typed over in the meantime is theirs.
 */
const nameStoryAfterDeck = (placeholderTitle: string, storyTitle: string) => {
  const title = storyTitle.trim();
  if (!title || title === placeholderTitle) return;
  const settings = (
    store.getState().story?.story as {
      settings?: { title?: unknown; og?: Record<string, unknown> };
    } | null
  )?.settings;
  if (!settings || settings.title !== placeholderTitle) return;
  store.dispatch(
    updateStorySettings({
      payload: {
        title,
        // The reader-facing name follows, as every rename path keeps it.
        og: { ...(settings.og ?? {}), siteName: title },
      },
      autosave: true,
    }),
  );
  nameVersionAfterDeck(title);
};

/**
 * The same name, written straight onto the version's record.
 *
 * A VERSION IS NAMED ON ITS RECORD, and the autosave the rename above asks
 * for is debounced -- leave the editor before it fires and the deck keeps its
 * real title while every list, the share dialog and the link still read
 * "document", the placeholder it was created under. This write does not wait.
 */
const nameVersionAfterDeck = (title: string) => {
  const version = store.getState().story?.version;
  if (!version?._id || version.data?.title === title) return;
  store
    .dispatch(
      versionsApi.endpoints.updateVersionInDatabase.initiate({
        versionId: version._id,
        payload: { data: { ...version.data, title } },
      }),
    )
    .unwrap()
    .catch((error: unknown) => {
      // The story is named either way; this only decides whether the name is
      // already on the record when the user walks away.
      console.error("Failed to name the version after its deck:", error);
    });
};

/** The slides of the open deck, in order. */
const deckSlideIds = (): string[] => {
  const layers = store.getState().story?.flatContentLayers as
    | Record<string, { childIds?: string[] }>
    | undefined;
  return layers?.root?.childIds ?? [];
};

export const DeckBuildRunner = () => {
  const { planDeck, generateSlideAt } = useFunnelGeneration();
  const { updatePublishedVersion } = usePublish();
  const job = useDeckBuildStore((s) => s.job);
  const status = useDeckBuildStore((s) => s.status);
  const attempt = useDeckBuildStore((s) => s.attempt);
  // One run per (job, run attempt). A retry flips status back to "building",
  // which is the signal to run again with the same job.
  const runningRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // The plan survives a retry: re-deciding the deck would cost a fetch and a
  // model call to arrive at the same answer, and a DIFFERENT answer would put
  // the retried slide in a design the rest of the deck is not in.
  const planRef = useRef<DeckPlan | null>(null);

  // FOLLOW THE BUILD, UNLESS THE USER IS LOOKING ELSEWHERE. Inserting a slide
  // selects it and scrolls to it, which is the "watch it build" effect the
  // editor opens for.
  //
  // It used to switch off for the rest of the run on the first pointerdown or
  // keydown anywhere -- and one stray click left the editor on the first
  // slide while every later one arrived out of sight. Following always went
  // too far the other way: a thumbnail clicked to read the cover was undone a
  // second later by the next slide. So looking around PAUSES the follow, and
  // FOLLOW_RESUME_MS without it picks the build back up. (The deck is
  // read-only while it builds, ./store/deck-build-lock, so this is only ever
  // about where the user is looking, never about an edit.)
  const isWriting = status === "planning" || status === "building";
  const lastLookAroundRef = useRef(0);
  useEffect(() => {
    if (!isWriting) return;
    lastLookAroundRef.current = 0;
    const note = (event: Event) => {
      if (event instanceof KeyboardEvent && !SCROLL_KEYS.has(event.key)) return;
      if (
        event.type === "pointerdown" &&
        event.target instanceof Element &&
        event.target.closest(`[${LIVE_ATTR}]`)
      ) {
        return;
      }
      lastLookAroundRef.current = Date.now();
    };
    for (const type of LOOK_AROUND_EVENTS) {
      document.addEventListener(type, note, { passive: true });
    }
    return () => {
      for (const type of LOOK_AROUND_EVENTS) {
        document.removeEventListener(type, note);
      }
    };
  }, [isWriting]);

  /**
   * Puts a slide into the open deck at `index`, and answers with the id it
   * landed under.
   *
   * The index is stated outright rather than derived from an anchor slide:
   * "insert after X" cannot express "insert at the front", and the empty
   * anchor that used to mean it fell through to the selected slide, which put
   * a late-arriving cover in the middle of the deck. The id has to be read
   * back from the deck rather than taken from the payload, because the insert
   * mints its own.
   */
  const placeSlide = useCallback(
    (
      slideJson: unknown,
      brandImport: unknown,
      index: number,
      locked = false,
      keepImages = false,
      follow = true,
    ) => {
      // The brand mark, the same substitution slide one got during story
      // creation. Without it a deck opens with the user's logo on the cover
      // and the template's "Logoipsum" on every slide after it. NOT on a
      // slide the user locked: "exactly as it is" includes its logo -- and
      // NOT when they asked to keep the template's original assets, for the
      // same reason. The logo is an asset, and the one on a document of their
      // own is very often the one they meant to keep; replacing it with the
      // brand from AI-Brain is the one thing that switch says not to do.
      // The story's resolved palette, so a slide painted in a theme variable
      // can be judged light or dark rather than guessed at.
      const designSystem = (
        store.getState().story?.story as
          | { settings?: { designSystemV2?: Record<string, unknown> } }
          | undefined
      )?.settings?.designSystemV2;
      const branded =
        locked || keepImages
          ? slideJson
          : applyBrandLogo([slideJson], brandImport as never, designSystem)
              .slides[0];
      const normalized = normalizedFromNestedStructured(
        [branded as StaticEntityType],
        "root",
        "slide",
      );
      store.dispatch(
        createEntity({
          id: "",
          index,
          entityEntryRootId: normalized.root,
          entities: normalized.entities,
          feature: "slides-library",
          // Not selected, so not scrolled to, while the user is looking at
          // another slide; see FOLLOW_RESUME_MS.
          skipActiveEntity: !follow,
        }),
      );
      return deckSlideIds()[index] ?? "";
    },
    [],
  );

  const run = useCallback(
    async (activeJob: DeckBuildJob) => {
      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;
      const state = useDeckBuildStore.getState();

      // PLANNED HERE, not before the editor opened. The outline, the
      // template's own slides and one design mapping per slide cost a fetch
      // and sometimes a model call; doing that ahead of navigation is exactly
      // the wait this design removes. Kept across a retry so it is paid once.
      const plan =
        planRef.current ?? (await planDeck(activeJob.inputs, signal));
      planRef.current = plan;
      if (signal.aborted) return;
      useDeckBuildStore.getState().planned(plan.pairs.length);

      // A retry runs only what failed; a first run does the whole deck,
      // because the story was created with a placeholder rather than a slide.
      const positions = state.failed.length
        ? [...state.failed].sort((a, b) => a - b)
        : Array.from({ length: plan.pairs.length }, (_, n) => n);
      clearFailedSlides();

      if (positions.length === 0) {
        useDeckBuildStore.getState().finish("done");
        return;
      }

      if (!(await waitForStory(activeJob, signal))) {
        // The editor never opened this story -- the user went somewhere else
        // before it loaded. Nothing to fill, and nowhere to say so.
        useDeckBuildStore.getState().finish("stopped");
        return;
      }
      nameStoryAfterDeck(activeJob.placeholderTitle, plan.storyTitle);

      // THE PLACEHOLDER THE STORY WAS CREATED WITH. A story cannot have zero
      // slides, so the blank one has to stay until there is a real slide to
      // replace it with -- deleting it first is refused by the store, and
      // rightly. Captured before anything is inserted; dropped after the first
      // real slide lands. Empty on a retry, when real slides are already here.
      // "Nothing real placed yet", NOT "position 0 placed": if the cover was
      // the slide that failed, keying on position 0 would treat a whole
      // finished deck as placeholders and delete every slide in it.
      const placeholders =
        Object.keys(useDeckBuildStore.getState().placedIds).length === 0
          ? [...deckSlideIds()]
          : [];
      // Told to the editor, which paints them blank until they are dropped
      // (editor/components/deck-build-shield). A copy: the list below is
      // emptied in place as they go.
      useDeckBuildStore.getState().notePlaceholders([...placeholders]);

      // WHERE EACH SLIDE WENT, which decides the slot the next one takes.
      //
      // Read from the store rather than started fresh, because a retry runs
      // after a whole deck has been placed and has to count against all of
      // it -- a retried slide belongs among the slides that overtook it, not
      // at the end.
      const placedAt = new Map<number, string>(
        Object.entries(useDeckBuildStore.getState().placedIds).map(
          ([at, id]) => [Number(at), id],
        ),
      );

      /**
       * Puts a slide in and plays it building, resolving when it has
       * finished. The caller awaits it, which is what keeps two slides from
       * ever being built at the same time.
       */
      const place = async (position: number, slideJson: unknown) => {
        // The user left the editor: the tree these slides go into is gone, so
        // stop rather than write into a document nobody is looking at.
        if (!storyIsOpen(activeJob)) {
          controller.abort();
          return;
        }
        let rootId = "";
        try {
          rootId = placeSlide(
            slideJson,
            activeJob.brandImport,
            slideIndexFor(placedAt, position),
            plan.pairs[position]?.locked,
            plan.keepImages,
            Date.now() - lastLookAroundRef.current >= FOLLOW_RESUME_MS,
          );
          // Hidden before the browser has painted it. The dispatch above only
          // SCHEDULES React's render, so the nodes do not exist yet and this
          // stylesheet is already in the document when they mount -- without
          // it the finished slide flashes whole for a frame and is then
          // animated back in, which is the "warping" this replaces.
          holdSubtreeInvisible(rootId);
          placedAt.set(position, rootId);
          useDeckBuildStore.getState().notePlaced(position, rootId);
          // The document now has something real in it; the blank slide it
          // opened with has done its job.
          while (placeholders.length) {
            store.dispatch(deleteEntity({ id: placeholders.pop() }));
          }
        } catch (e) {
          // One slide the editor refuses is one slide missing, not a dead
          // deck. It joins the retryable failures and the rest carry on.
          console.error(`Slide ${position} could not be inserted`, e);
          releaseHold();
          noteFailedSlide(position);
          return;
        }
        useDeckBuildStore
          .getState()
          .markPlaced(useDeckBuildStore.getState().placed + 1);

        if (skipBuildAnimation()) {
          releaseHold();
          await delay(REDUCED_MOTION_INTERVAL_MS, signal);
          return;
        }

        await afterTwoFrames();
        if (signal.aborted) {
          releaseHold();
          return;
        }
        // The hold comes off and the animation goes on together, so the
        // elements are never briefly unstyled.
        const built = buildUnitsOf(
          rootId,
          store.getState().story?.flatContentLayers as Parameters<
            typeof buildUnitsOf
          >[1],
        );
        const buildMs = revealArriving(built, {
          stepMs: SLIDE_BUILD_STEP_MS,
          maxSteps: SLIDE_BUILD_MAX_STEPS,
        });
        releaseHold();
        await delay(buildMs + SLIDE_SETTLE_MS, signal);
        // This slide is finished; take the animation off it so scrolling back
        // to it later does not play it again.
        clearArriving(built);
      };

      // TOP TO BOTTOM, ONE AT A TIME.
      //
      // Slides are generated twenty at once and come back in any order, but
      // the document must never show slide 5 while 1 is still missing -- a
      // deck that starts at its second slide and fills in the cover later
      // looks broken even though it ends up right. So the reveal walks the
      // positions in order: the next one due goes in as soon as it has
      // arrived, and nothing after it goes in before it.
      //
      // The pacing is what keeps that from turning into a stall-then-dump.
      // While slide 3 is slow the ones behind it pile up, and releasing the
      // backlog at once is exactly the burst this is meant to avoid -- so the
      // queue drains one slide every REVEAL_INTERVAL_MS, which reads as the
      // deck being written rather than pasted.
      //
      // A position that failed every attempt never arrives, and must not hold
      // the rest of the deck behind it forever; it counts as settled and the
      // queue moves on.
      const waiting = new Map<number, unknown>();
      const failedPositions = new Set<number>();
      let generating = true;
      let cursor = 0;

      const reveal = (async () => {
        while (!signal.aborted) {
          const step = nextRevealStep({
            positions,
            cursor,
            arrived: new Set(waiting.keys()),
            failed: failedPositions,
            generating,
          });
          if (step.action === "done") return;
          if (step.action === "wait") {
            await delay(REVEAL_POLL_MS, signal);
            continue;
          }
          cursor += 1;
          if (step.action === "skip") continue;
          const slideJson = waiting.get(step.position);
          waiting.delete(step.position);
          // Awaited: the next slide does not begin until this one has
          // finished building.
          await place(step.position, slideJson);
        }
      })();

      await runWithConcurrency(
        positions.length,
        SLIDE_GENERATION_CONCURRENCY,
        (n) => generateSlideAt(plan, positions[n], signal),
        undefined,
        signal,
        (n, slideJson) => {
          if (!slideJson) {
            noteFailedSlide(positions[n]);
            failedPositions.add(positions[n]);
            return;
          }
          waiting.set(positions[n], slideJson);
        },
      );
      generating = false;
      await reveal;

      if (signal.aborted) {
        // Stop and navigate-away both land here; whichever set the status has
        // already said which it was.
        if (useDeckBuildStore.getState().status !== "idle") {
          useDeckBuildStore.getState().finish("stopped");
        }
        return;
      }

      const failed = useDeckBuildStore.getState().failed.length;
      useDeckBuildStore.getState().finish(failed > 0 ? "partial" : "done");
      if (failed === 0) {
        toast.success("Your document is ready");
      }
      // THE LINK IS RENDERED WHEN THE VERSION IS MADE, and the version is made
      // before the first slide is written -- so the page a recipient would
      // open is the blank one the deck was built out of, under the
      // placeholder name, until something renders it again. "Update & Share"
      // does that, and a user who is happy with their deck has no reason to
      // go looking for it. The deck ends by doing it for them.
      //
      // Only a version has a link of its own to refresh; on a story path this
      // returns without doing anything (see providers/hooks/use-publish).
      await updatePublishedVersion();
    },
    [planDeck, generateSlideAt, placeSlide, updatePublishedVersion],
  );

  /**
   * The run, guaranteed to leave "building" behind it.
   *
   * The effect below re-enters whenever the status is "building" and no run is
   * in flight, so a run that threw its way out -- a malformed slide breaking
   * the insert, say -- would be started again immediately, and again, forever.
   * Whatever happens, the status moves on.
   */
  const runOnce = useCallback(
    async (activeJob: DeckBuildJob, key: string) => {
      // A RETIRED RUN TOUCHES NOTHING. The status and the animation belong to
      // whichever run is current, and a run that was aborted to make way for
      // a new deck reaches this block LATE -- its "stopped" would land on the
      // deck that replaced it, and its clearAllArriving() would strip the
      // animation off the slides arriving right now.
      const isCurrent = () => runningRef.current === key;
      try {
        await run(activeJob);
      } catch (e) {
        console.error("Deck build stopped unexpectedly", e);
        if (isCurrent()) {
          const failed = useDeckBuildStore.getState().failed.length;
          useDeckBuildStore
            .getState()
            .finish(failed > 0 ? "partial" : "stopped");
        }
      } finally {
        if (isCurrent()) {
          // Whatever happened -- finished, stopped, threw -- nothing may be
          // left mid-animation, or it replays when the user scrolls back to it.
          releaseHold();
          clearAllArriving();
          const status = useDeckBuildStore.getState().status;
          if (status === "planning" || status === "building") {
            useDeckBuildStore.getState().finish("stopped");
          }
        }
      }
    },
    [run],
  );

  // A different document is a different deck: the cached plan must not outlive
  // the job it was made for. Keyed by the version as well as the story --
  // see jobKey.
  const plannedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (job && plannedForRef.current !== jobKey(job)) {
      plannedForRef.current = jobKey(job);
      planRef.current = null;
    }
  }, [job]);

  useEffect(() => {
    if (!job || (status !== "planning" && status !== "building")) return;
    // Identity of THIS run: the same deck retried is a new run, so the key
    // carries the attempt as well as the document. It has to hold still for
    // the whole run -- keying on the failure list instead meant the key
    // changed the moment a slide failed, and the effect started a SECOND run
    // beside the first, replanning the deck and fighting it for the document.
    // The document is story AND version (jobKey): keyed by story alone, a
    // second standalone version -- which shares its host page with the first
    // -- looked like a run already in flight and never got one.
    const key = `${jobKey(job)}:${attempt}`;
    if (runningRef.current === key) return;
    // THE RUN BEFORE THIS ONE IS OVER. Starting a second deck without ending
    // the first left both alive on one abortRef and one status: the old run
    // went on inserting into whatever document was open, and when it noticed
    // the navigation away it called finish("stopped") -- against the NEW
    // build's state, stopping a deck that had only just begun.
    abortRef.current?.abort();
    runningRef.current = key;
    void runOnce(job, key).finally(() => {
      // Only the current run clears the slot; a retired one finishing later
      // must not open the door for a duplicate of the run now in flight.
      if (runningRef.current === key) runningRef.current = null;
    });
  }, [job, status, attempt, runOnce]);

  // Stop is a status change, not a call: the banner can set it without
  // reaching into the run.
  useEffect(() => {
    if (status === "stopped") abortRef.current?.abort();
  }, [status]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return null;
};

export default DeckBuildRunner;
